import { useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { IFormInput, Steps } from "../../types";
import { FormProvider, useForm } from "react-hook-form";
import { AddDependent } from "../AddDependent";
import { DependentRegister } from "../DependentRegister";
import { SummaryDependent } from "../SummaryDependent";
import { DependentList } from "../DependentList";
import { useSessionContext } from "../../../../contexts/SessionContext";
import { LegalGuardianList } from "../LegalGuardianList";
import { MobileBottomSheet } from "@/components/common/MobileBottomSheet";

const defaultValues = {
  name: "",
  identifier: "",
  birthdate: undefined,
  gender: "",
};

type SheetStep =
  | Steps.S2DependentRegister
  | Steps.S3SummaryDependent;

export function Form() {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const { userPatient, guardianRelated } = useSessionContext();
  const [page, setPage] = useState(Steps.S1AddDependent);
  const [minorPatient, setMinorPatient] = useState<boolean>(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetStep, setSheetStep] = useState<SheetStep>(
    Steps.S2DependentRegister,
  );

  const methods = useForm<IFormInput>({ defaultValues });
  const { handleSubmit, control, setValue, reset } = methods;

  const listSection = (
    <>
      <AddDependent onClick={handleOpenAddDependent} />
      {userPatient?.hasOwnProperty("link") && <DependentList />}
      {guardianRelated ? <LegalGuardianList /> : null}
    </>
  );

  function handleOpenAddDependent() {
    if (isMobileLayout) {
      setSheetStep(Steps.S2DependentRegister);
      setSheetOpen(true);
      return;
    }
    setPage(Steps.S2DependentRegister);
  }

  function handleCloseSheet() {
    setSheetOpen(false);
    setSheetStep(Steps.S2DependentRegister);
    reset(defaultValues);
  }

  function handleRegisterBack() {
    if (isMobileLayout) {
      handleCloseSheet();
      return;
    }
    setPage(Steps.S1AddDependent);
  }

  function handleRegisterNext() {
    if (isMobileLayout) {
      setSheetStep(Steps.S3SummaryDependent);
      return;
    }
    setPage(Steps.S3SummaryDependent);
  }

  function handleSummaryBack() {
    if (isMobileLayout) {
      setSheetStep(Steps.S2DependentRegister);
      return;
    }
    setPage(Steps.S2DependentRegister);
  }

  function handleSummaryComplete() {
    reset(defaultValues);
    if (isMobileLayout) {
      setSheetOpen(false);
      setSheetStep(Steps.S2DependentRegister);
      return;
    }
    setPage(Steps.S1AddDependent);
  }

  const registerStep = (
    <DependentRegister
      control={control}
      handleSubmit={handleSubmit}
      onBackButtonClick={handleRegisterBack}
      navigateToNextPage={handleRegisterNext}
      setValue={setValue}
      setMinorPatient={setMinorPatient}
    />
  );

  const summaryStep = (
    <SummaryDependent
      reset={reset}
      control={control}
      handleSubmit={handleSubmit}
      onBackButtonClick={handleSummaryBack}
      navigateToNextPage={handleSummaryComplete}
      setValue={setValue}
      minorPatient={minorPatient}
    />
  );

  const sheetTitle =
    sheetStep === Steps.S3SummaryDependent
      ? "Finalize o cadastro"
      : "Adicionar dependente";

  const sheetSubtitle =
    sheetStep === Steps.S3SummaryDependent
      ? "Confirme os dados do dependente"
      : "Informe o CPF e o grau de parentesco";

  return (
    <FormProvider {...methods}>
      {isMobileLayout ? (
        listSection
      ) : (
        <>
          {page === Steps.S1AddDependent && listSection}
          {page === Steps.S2DependentRegister && registerStep}
          {page === Steps.S3SummaryDependent && summaryStep}
        </>
      )}

      {isMobileLayout && (
        <MobileBottomSheet
          open={sheetOpen}
          onClose={handleCloseSheet}
          title={sheetTitle}
          subtitle={sheetSubtitle}
          size="large"
        >
          {sheetStep === Steps.S2DependentRegister && registerStep}
          {sheetStep === Steps.S3SummaryDependent && summaryStep}
        </MobileBottomSheet>
      )}
    </FormProvider>
  );
}
